import { useState, type FormEvent } from 'react';
import {
  useDevices, useSaveDevice, useDeleteDevice,
} from '../../lib/api/hooks-extension.js';
import { useExtensions } from '../../lib/api/hooks.js';
import { TableSkeleton, Empty, ErrorBanner, Badge } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { ApiClientError } from '../../lib/api/client.js';
import { relativeTime } from '../../lib/format.js';
import type { Device } from '@arcnet/contracts';

/**
 * Handset inventory.
 *
 * A device is separate from an extension because the two change independently:
 * a phone is re-assigned between people without the extension moving, and one
 * extension can ring on a desk phone and a softphone at once. The list is
 * therefore keyed on the MAC — the thing physically on someone's desk.
 */

const VENDORS = [
  { key: 'yealink', label: 'Yealink' },
  { key: 'grandstream', label: 'Grandstream' },
  { key: 'fanvil', label: 'Fanvil' },
  { key: 'polycom', label: 'Poly / Polycom' },
  { key: 'snom', label: 'Snom' },
  { key: 'cisco', label: 'Cisco' },
  { key: 'generic', label: 'Other' },
] as const;

const STATUS_TONE: Record<string, 'ok' | 'warn' | 'danger' | 'muted'> = {
  online: 'ok', provisioned: 'ok', offline: 'warn',
  unprovisioned: 'muted', retired: 'muted',
};

interface LineDraft { id: string; extensionId: string; label: string }

export function DevicesTab({ tenantId }: { tenantId: string }) {
  const devices = useDevices(tenantId);
  const del = useDeleteDevice(tenantId);
  const [editing, setEditing] = useState<Device | null>(null);
  const [creating, setCreating] = useState(false);

  const rows = devices.data?.data ?? [];
  const unprovisioned = rows.filter((d) => !d.mac_address).length;

  return (
    <>
      <div className="arc-row" style={{ marginBottom: 14 }}>
        <div>
          <h2 className="arc-h2">Handsets</h2>
          <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 2 }}>
            Physical phones and the extensions on their line keys.
          </p>
        </div>
        <div className="arc-spacer" />
        <button type="button" className="arc-btn arc-btn-primary arc-btn-sm"
          onClick={() => { setCreating((v) => !v); setEditing(null); }}>
          <Icon name={creating ? 'close' : 'plus'} size={13} />
          {creating ? 'Cancel' : 'Add handset'}
        </button>
      </div>

      {devices.error && <ErrorBanner error={devices.error} />}
      {del.error && <ErrorBanner error={del.error} />}

      {unprovisioned > 0 && !creating && !editing && (
        <div className="arc-banner arc-banner-info" style={{ marginBottom: 14 }}>
          <Icon name="info" size={14} />
          <span>
            {unprovisioned} handset{unprovisioned === 1 ? ' has' : 's have'} no MAC address, so
            {unprovisioned === 1 ? ' it' : ' they'} cannot fetch a configuration. Add the MAC from
            the label on the underside of the phone.
          </span>
        </div>
      )}

      {(creating || editing) && (
        <DeviceEditor
          tenantId={tenantId} existing={editing}
          onDone={() => { setCreating(false); setEditing(null); }}
        />
      )}

      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Handset</th><th>MAC</th><th>Model</th>
                <th>Lines</th><th>Status</th><th>Last seen</th><th />
              </tr>
            </thead>
            {devices.isLoading ? <TableSkeleton cols={7} /> : (
              <tbody>
                {rows.map((d) => (
                  <tr key={d.id}>
                    <td style={{ fontWeight: 550 }}>
                      {d.label ?? <span className="arc-subtle">unnamed</span>}
                    </td>
                    <td className="arc-mono">
                      {d.mac_address ?? <span className="arc-subtle">not set</span>}
                    </td>
                    <td className="arc-muted">
                      {[VENDORS.find((v) => v.key === d.vendor)?.label, d.model]
                        .filter(Boolean).join(' ') || <span className="arc-subtle">—</span>}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {d.lines.length === 0
                          ? <span className="arc-subtle">none</span>
                          : d.lines.map((l) => (
                              <Badge key={l.id} tone="muted">
                                <span className="arc-mono" style={{ fontSize: 11 }}>
                                  {l.extension_number}
                                </span>
                                {l.extension_name ? ` ${l.extension_name}` : ''}
                              </Badge>
                            ))}
                      </div>
                    </td>
                    <td>
                      <Badge tone={STATUS_TONE[d.status] ?? 'muted'}>
                        <span className="arc-dot" aria-hidden="true" />{d.status}
                      </Badge>
                    </td>
                    <td className="arc-muted">
                      {d.last_seen_at
                        ? relativeTime(d.last_seen_at)
                        : <span className="arc-subtle">never</span>}
                    </td>
                    <td className="arc-td-right">
                      <div className="arc-row arc-row-actions" style={{ justifyContent: 'flex-end', gap: 2 }}>
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                          onClick={() => { setEditing(d); setCreating(false); }}>Edit</button>
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                          onClick={() => {
                            if (confirm(`Remove ${d.label ?? d.mac_address ?? 'this handset'}? The phone will stop receiving configuration.`)) {
                              del.mutate(d.id);
                            }
                          }}>Remove</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {!devices.isLoading && rows.length === 0 && (
          <Empty icon="phone" title="No handsets"
            hint="Add a phone by its MAC address and assign extensions to its line keys." />
        )}
      </div>
    </>
  );
}

function DeviceEditor({ tenantId, existing, onDone }: {
  tenantId: string; existing: Device | null; onDone: () => void;
}) {
  const save = useSaveDevice(tenantId);
  const extensions = useExtensions(tenantId, { limit: 500 });

  const [mac, setMac] = useState(existing?.mac_address ?? '');
  const [vendor, setVendor] = useState<string>(existing?.vendor ?? 'yealink');
  const [model, setModel] = useState(existing?.model ?? '');
  const [label, setLabel] = useState(existing?.label ?? '');
  const [lines, setLines] = useState<LineDraft[]>(() =>
    existing?.lines.map((l) => ({
      id: l.id, extensionId: l.extension_id, label: l.label ?? '',
    })) ?? [{ id: crypto.randomUUID(), extensionId: '', label: '' }],
  );

  const err = save.error instanceof ApiClientError ? save.error : null;
  const macNormalised = mac.replace(/[^0-9a-fA-F]/g, '');
  const macValid = mac === '' || macNormalised.length === 12;

  return (
    <form
      className="arc-card arc-card-body" style={{ marginBottom: 14 }}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        save.mutate({
          ...(existing ? { id: existing.id } : {}),
          mac_address: mac || null,
          vendor, model: model || null, label: label || null,
          lines: lines
            .filter((l) => l.extensionId)
            .map((l, i) => ({
              extension_id: l.extensionId,
              line_number: i + 1,
              ...(l.label ? { label: l.label } : {}),
            })),
        }, { onSuccess: onDone });
      }}
    >
      <h3 className="arc-h2" style={{ marginBottom: 12 }}>
        {existing ? `Edit ${existing.label ?? existing.mac_address ?? 'handset'}` : 'Add handset'}
      </h3>
      {err && (
        <div className="arc-banner arc-banner-danger" style={{ marginBottom: 12 }} role="alert">
          <Icon name="warning" size={14} /><span>{err.message}</span>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="dv-label">Name</label>
          <input id="dv-label" className="arc-input" value={label}
            onChange={(e) => setLabel(e.target.value)} placeholder="Reception desk" />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="dv-mac">MAC address</label>
          <input id="dv-mac" className="arc-input arc-mono" value={mac}
            aria-invalid={!macValid}
            onChange={(e) => setMac(e.target.value)} placeholder="805e:c04d:1f2a" />
          {/* The MAC is what identifies the phone when it asks for its config,
              so a typo here is a phone that silently never provisions. */}
          <p className={macValid ? 'arc-hint' : 'arc-error'}>
            {macValid ? 'Printed on the underside of the phone.' : 'Needs 12 hex digits.'}
          </p>
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="dv-vendor">Vendor</label>
          <select id="dv-vendor" className="arc-input" value={vendor}
            onChange={(e) => setVendor(e.target.value)}>
            {VENDORS.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
          </select>
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="dv-model">Model</label>
          <input id="dv-model" className="arc-input" value={model}
            onChange={(e) => setModel(e.target.value)} placeholder="T46S" />
        </div>
      </div>

      <div className="arc-field">
        <div className="arc-row" style={{ marginBottom: 7 }}>
          <label className="arc-label" style={{ margin: 0 }}>Line keys</label>
          <div className="arc-spacer" />
          <button type="button" className="arc-btn arc-btn-sm"
            onClick={() => setLines((p) => [
              ...p, { id: crypto.randomUUID(), extensionId: '', label: '' },
            ])}>
            <Icon name="plus" size={12} /> Add line
          </button>
        </div>
        {/* Order IS the line number: line 1 is the top key on the handset, so
            the list reads the way the phone looks. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          {lines.map((l, i) => (
            <div key={l.id} className="arc-row" style={{ gap: 8 }}>
              <span className="arc-key-chip" style={{ width: 24, height: 24, fontSize: 11.5 }}>
                {i + 1}
              </span>
              <select className="arc-input" style={{ maxWidth: 300 }} value={l.extensionId}
                aria-label={`Line ${i + 1} extension`}
                onChange={(e) => setLines((p) =>
                  p.map((y) => y.id === l.id ? { ...y, extensionId: e.target.value } : y))}>
                <option value="">— not assigned —</option>
                {extensions.data?.data.map((e) => (
                  <option key={e.id} value={e.id}>{e.number} · {e.display_name}</option>
                ))}
              </select>
              <input className="arc-input" style={{ maxWidth: 200 }} placeholder="Key label (optional)"
                value={l.label} aria-label={`Line ${i + 1} label`}
                onChange={(e) => setLines((p) =>
                  p.map((y) => y.id === l.id ? { ...y, label: e.target.value } : y))} />
              <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon"
                aria-label={`Remove line ${i + 1}`}
                onClick={() => setLines((p) => p.filter((y) => y.id !== l.id))}>
                <Icon name="trash" size={13} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <button type="submit" className="arc-btn arc-btn-primary" disabled={save.isPending || !macValid}>
        {save.isPending ? 'Saving…' : existing ? 'Save changes' : 'Add handset'}
      </button>
    </form>
  );
}
